import {
  CAMPAIGN_JOIN_MODES,
  CAMPAIGN_SCOPES,
  CAMPAIGN_TRIGGERS,
  NO_ORDER_MIN_AGE_DAYS,
} from '#/entities/campaign'
import type {
  CampaignJoinMode,
  CampaignScope,
  CampaignTrigger,
} from '#/entities/campaign'
import { SHIPMENT_STAGES } from '#/entities/shipment'
import {
  CampaignEnrollmentMode,
  CampaignKind,
  CampaignScope as ApiCampaignScope,
  TriggerEvent,
} from '#/shared/api/generated/communication/models'
import type { CampaignCreate } from '#/shared/api/generated/communication/models'

/** The trigger chips that are real service events (see `TRIGGER_EVENTS`). */
export type CampaignEventTrigger = Exclude<
  CampaignTrigger,
  typeof CAMPAIGN_TRIGGERS.noOrder60d
>

/**
 * Which service event each trigger chip means.
 *
 * "No order in 60 days" is deliberately absent: the absence of activity is not
 * an event and would never fire. The design still shows it as a fourth chip
 * beside the three real ones — honestly, because it enrols people over time
 * just like they do — so that chip is wired to a rolling **filter** on
 * `min_age_days` instead. Four chips, two mechanisms.
 */
export const TRIGGER_EVENTS: Record<CampaignEventTrigger, TriggerEvent> = {
  [CAMPAIGN_TRIGGERS.whenQuoted]: TriggerEvent.shipmentquotecreated,
  [CAMPAIGN_TRIGGERS.dispatched]: TriggerEvent.shipmentorderdispatched,
  [CAMPAIGN_TRIGGERS.delivered]: TriggerEvent.shipmentdelivered,
}

/** The chip a saved `trigger_event` reads back as. The read model types the
 *  event as a bare string, so an unknown one reads as no chip. */
export function triggerFromEvent(
  event: string | null | undefined,
): CampaignEventTrigger | null {
  const found = (
    Object.entries(TRIGGER_EVENTS) as [CampaignEventTrigger, TriggerEvent][]
  ).find(([, value]) => value === event)
  return found == null ? null : found[0]
}

const ENROLLMENT_MODES: Record<CampaignJoinMode, CampaignEnrollmentMode> = {
  [CAMPAIGN_JOIN_MODES.manual]: CampaignEnrollmentMode.manual,
  [CAMPAIGN_JOIN_MODES.filter]: CampaignEnrollmentMode.filter,
  [CAMPAIGN_JOIN_MODES.trigger]: CampaignEnrollmentMode.trigger,
}

export interface NewCampaignInput {
  name: string
  scope: CampaignScope
  join: CampaignJoinMode
  trigger: CampaignTrigger
  filterDefinition: Record<string, unknown>
  /** "Pick users" — empty means the whole organization. */
  userIds: string[]
  /** "Pick sources" — empty means every source. */
  providerIds: string[]
}

/** A rolling filter narrowed to the order stage — a single chosen stage or
 *  a list of them, whichever the service published the field as. */
function filtersOrderStage(definition: Record<string, unknown>): boolean {
  const stage = definition['stage']
  const stages = Array.isArray(stage) ? stage : [stage]
  return stages.includes(SHIPMENT_STAGES.ORDER)
}

export function toApiCampaignScope(scope: CampaignScope): ApiCampaignScope {
  return scope === CAMPAIGN_SCOPES.organization
    ? ApiCampaignScope.organization
    : ApiCampaignScope.personal
}

/** A saved scope, as the dialogs' own vocabulary. */
export function fromApiCampaignScope(scope: ApiCampaignScope): CampaignScope {
  return scope === ApiCampaignScope.organization
    ? CAMPAIGN_SCOPES.organization
    : CAMPAIGN_SCOPES.justMe
}

/**
 * What the dialog's three answers add up to on `POST /campaigns`.
 *
 * `kind` is always sent, never left to the service's default. It decides how
 * someone who is already in behaves (communication#228): a `sequence` takes a
 * person once, for life; an `automation` takes every event as its own entry.
 * A trigger chip fires on something that happens to a quote — quoted,
 * dispatched, delivered — and can happen again to the same customer, so it is
 * an automation: left as the default `sequence`, a second delivery for the
 * same customer was silently blocked (67.8% of sends lost when the old
 * automations were converted). Manual picks and rolling filters enrol a
 * person, not an event, and stay sequences — "No order 60d" among them, since
 * it is a filter underneath.
 *
 * The two exit rules are sent for the same reason `kind` is: the service
 * defaults both to `true`, and neither default suits what this product sends.
 */
export function toCampaignCreate(input: NewCampaignInput): CampaignCreate {
  const body: CampaignCreate = {
    name: input.name.trim(),
    scope: toApiCampaignScope(input.scope),
    kind: CampaignKind.sequence,
    enrollment_mode: ENROLLMENT_MODES[input.join],
    applies_to_user_ids: input.userIds.length > 0 ? input.userIds : undefined,
    provider_ids: input.providerIds.length > 0 ? input.providerIds : undefined,
    // The link in one of our messages is the quote or the tracking page, never
    // a marketing call-to-action — a click is interest, and `exit_on_booking`
    // already covers conversion. The service's `true` would end the sequence
    // on the exact action the Engagement card measures as success ("Reached
    // the quote page", "Median time to click").
    exit_on_click: false,
    // A booked order does mean they converted, so this one stays on.
    exit_on_booking: true,
  }
  if (input.join === CAMPAIGN_JOIN_MODES.trigger) {
    if (input.trigger === CAMPAIGN_TRIGGERS.noOrder60d) {
      body.enrollment_mode = CampaignEnrollmentMode.filter
      body.filter_definition = { min_age_days: NO_ORDER_MIN_AGE_DAYS }
      return body
    }
    body.kind = CampaignKind.automation
    body.trigger_event = TRIGGER_EVENTS[input.trigger]
    // Dispatched and delivered are order-stage events: whoever the event
    // enrolled booked before they joined, so "a booked order takes them out"
    // describes something already in the past. Only "when quoted" enrols
    // somebody who still has a booking ahead of them.
    body.exit_on_booking = input.trigger === CAMPAIGN_TRIGGERS.whenQuoted
  }
  if (input.join === CAMPAIGN_JOIN_MODES.filter) {
    body.filter_definition = input.filterDefinition
    // Same reasoning as the order-stage triggers: a filter on the order stage
    // enrols people who have already booked.
    if (filtersOrderStage(input.filterDefinition)) {
      body.exit_on_booking = false
    }
  }
  return body
}
